import React, { useContext, useEffect, useState } from 'react'

import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { AlarpMethod, SystemProperty } from 'plugin-codaf-risk-ext-bowtie/types/enums'

import { NoInfo, SettingsContainer } from '../BowtieSettings/styles'
import { Scrollbars } from '../generic'
import { useDimensions } from '../hooks'
import { getSystemProperty } from '../services'
import {
    BowtieBarrierSettingsRouteContext,
    IBowtieBarrierSettingsRouteContext,
    IQueryParamsEdit,
    IQueryParamsList
} from './BowtieBarrierSettingWrapper'
import { BarrierStatusName } from './components'
import { AlarpLevels } from './components/AlarpLevels/AlarpLevels'
import { BarrierCategories } from './components/BarrierCategories/BarrierCategories'
import { CategoryEditor } from './components/CategoryEditor/CategoryEditor'
import { Operations } from './components/Operations/Operations'

export const BowtieBarrierSettings: React.FC = () => {
    const { params, setParams } = useContext<IBowtieBarrierSettingsRouteContext<IQueryParamsEdit | IQueryParamsList>>(
        BowtieBarrierSettingsRouteContext
    ) // eslint-disable-line prettier/prettier, max-len
    const [systemProperty, setSystemProperty] = useState<string>(null)
    const [missingSystemProperty, setMissingSystemProperty] = useState<boolean>(false)
    const dimensions = useDimensions()
    const permission = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const [loading, setLoading] = useState<boolean>(false)

    const handleGetSystemProperty = async (): Promise<void> => {
        try {
            setLoading(true)
            const data = await getSystemProperty()
            if (data.property === SystemProperty.ABSOLUTE || data.property === SystemProperty.RELATIVE) {
                setMissingSystemProperty(false)
                setSystemProperty(data.property)
            }
        } catch (err) {
            setMissingSystemProperty(true)
            globalNotification.error(
                err.message ??
                    i18n('É necessário alterar o tipo de sistema para absoluto ou relativo antes de continuar')
            )
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        handleGetSystemProperty()
    }, [])

    return loading ? (
        <SettingsContainer>
            <NoInfo>
                <i className="fa fa-spinner fa-spin" />
            </NoInfo>
        </SettingsContainer>
    ) : missingSystemProperty ? (
        <NoInfo>
            <h1>{i18n('É necessário alterar o tipo de sistema para absoluto ou relativo antes de continuar')}.</h1>
        </NoInfo>
    ) : (
        <SettingsContainer>
            {permission ? (
                params.type === 'list' && (
                    <>
                        <div className="settings-header-wrapper">
                            <div className="container settings-header">
                                <div
                                    className={`settings-header-item ${params.tab === 'barrierStatus' ? 'active' : ''}`}
                                    onClick={() =>
                                        setParams({
                                            type: 'list',
                                            tab: 'barrierStatus',
                                            page: 1,
                                            perPage: 50,
                                            direction: 'ASC',
                                            orderBy: 'ID'
                                        })
                                    }
                                >
                                    {i18n('Barrier Status')}
                                </div>
                                <div
                                    className={`settings-header-item ${
                                        params.tab === 'barrierCategories' ? 'active' : ''
                                    }`}
                                    onClick={() =>
                                        setParams({
                                            type: 'list',
                                            tab: 'barrierCategories',
                                            page: 1,
                                            perPage: 50,
                                            direction: 'ASC',
                                            orderBy: 'ID'
                                        })
                                    }
                                >
                                    {i18n('Barrier Categories')}
                                </div>
                                <div
                                    className={`settings-header-item ${params.tab === 'alarpLevels' ? 'active' : ''}`}
                                    onClick={() =>
                                        setParams({
                                            type: 'list',
                                            tab: 'alarpLevels',
                                            page: 1,
                                            perPage: 50,
                                            direction: 'ASC',
                                            orderBy: 'ID'
                                        })
                                    }
                                >
                                    {i18n('ALARP Levels')}
                                </div>
                                <div
                                    className={`settings-header-item ${params.tab === 'operations' ? 'active' : ''}`}
                                    onClick={() =>
                                        setParams({
                                            type: 'list',
                                            tab: 'operations',
                                            page: 1,
                                            perPage: 50,
                                            direction: 'ASC',
                                            orderBy: 'ID'
                                        })
                                    }
                                >
                                    {i18n('Operations')}
                                </div>
                            </div>
                        </div>
                        <Scrollbars height={dimensions.height} className="settings-lists-wrapper">
                            <div className="container settings-lists">
                                {params.tab === 'barrierStatus' && <BarrierStatusName />}
                                {params.tab === 'barrierCategories' && (
                                    <BarrierCategories systemProperty={systemProperty} />
                                )}
                                {params.tab === 'alarpLevels' && <AlarpLevels />}
                                {params.tab === 'operations' && <Operations />}
                            </div>
                        </Scrollbars>
                    </>
                )
            ) : (
                <div className="permissionAlert">
                    <h1>{i18n('YOU DO NOT HAVE PERMISSION TO ACCESS THE BOWTIE SETTINGS.')}</h1>
                </div>
            )}
            {params.type === 'edit' && <>{params.tab === 'barrierCategories' && <CategoryEditor />}</>}
        </SettingsContainer>
    )
}
